import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Which leg the operator pinned. `from` = "the client spends `amount` of `from`"
 * (the backend derives toAmount); `to` = "the client receives `amount` of `to`"
 * (the backend derives fromAmount). The backend defaults to `from`.
 */
export type FxFixedSide = 'from' | 'to';

/**
 * pending_confirmation (raised, funds frozen, auto-cancels 5 min after `created`)
 *   -> new (the client accepted; the timer no longer applies)
 *   -> filled ("Settled" in FX wording) | cancelled
 *
 * There is no in_progress/finalized stage — do not reuse the OTC status set.
 *
 * A self-service conversion over the desk's approval threshold skips
 * `pending_confirmation` entirely and starts at `new` — the client chose the terms
 * by submitting it, so there is nothing left for them to accept. Those never expire;
 * see `isFxClientApproval` in fx-order.helper.ts.
 *
 * An operator quote raised with `requireConfirmation: false` starts at `new` for the
 * same reason: the terms were agreed off-platform, so there is no ask and no expiry.
 */
export type FxOrderStatus =
  | 'pending_confirmation'
  | 'new'
  | 'filled'
  | 'cancelled';

export interface FxEstimateRequest {
  from: string;
  to: string;
  amount: number;
  /** Always "`to` per 1 `from`", whatever fixedSide is. Never invert it. */
  rate: number;
  fixedSide?: FxFixedSide;
}

/**
 * `amount` is the leg that was typed; `result` is the leg the backend derived,
 * already rounded DOWN to the target currency's amountPrecision. Never recompute
 * `result` in the browser — `amount * rate` in JavaScript disagrees with what the
 * backend books. `pair` is the canonical published direction and may be the
 * reverse of from/to.
 */
export interface FxEstimateResponse {
  base: string;
  quote: string;
  pair: string;
  amount: string;
  rate: string;
  result: string;
  fee: string;
}

export interface CreateFxOrderRequest {
  userId: string;
  from: string;
  to: string;
  amount: number;
  rate: number;
  fixedSide?: FxFixedSide;
  /**
   * Whether the client has to accept the quote. The backend defaults it to `true`:
   * the order is raised as `pending_confirmation` and auto-cancels 5 minutes later
   * if they do not accept.
   *
   * `false` raises it straight into `new` on terms already agreed off-platform —
   * no confirmation ask, no expiry. The desk still has to settle it with
   * confirmFxOrderByAdmin().
   *
   * Forced to `false` when `forwardDate` is set.
   */
  requireConfirmation?: boolean;
  /**
   * Value date for a forward FX order (`YYYY-MM-DD`, strictly after today in the
   * quote-day timezone). When set: no client confirm, no freeze at create, settles
   * on/after this date via poller or manual settle.
   */
  forwardDate?: string;
}

/**
 * Money fields are decimal strings — coerce with Number() at the point of use.
 *
 * `symbol` / `side` / `quantity` / `price` are inherited generic-order fields keyed
 * on the canonical `pair`, which can be the REVERSE of fromCurrency/toCurrency (a
 * USD -> EUR order can legitimately carry pair "EUR/USD"). Render
 * fromCurrency / toCurrency / fromAmount / toAmount / rate instead.
 *
 * `extra.fxToAmount` is serialized the same way as the fields above (a decimal
 * string, === `toAmount`); it is a raw scaled integer in backend storage only.
 */
export interface FxOrder {
  orderID: string;
  userId: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string;
  rate: string;
  frozenQuantity: string;
  frozenCurrency: string;
  pair: string;
  status: FxOrderStatus;
  statusHistory?: any[];
  created: string;
  updated: string;
  /** True when raised with a value date — no freeze until that date. */
  isForward?: boolean;
  /** Value date `YYYY-MM-DD` when `isForward`. */
  forwardDate?: string | null;
  /** Inherited generic-order fields — present but misleading, see above. */
  symbol?: string;
  side?: 'buy' | 'sell';
  type?: string;
  subType?: string;
  quantity?: string;
  price?: string;
  cumQuoteQuantity?: string;
  extra?: {
    isFx?: boolean;
    /** Set on operator quotes. It is what subjects the order to the 5-minute sweep. */
    createdByAdmin?: boolean;
    /**
     * Set on a self-service conversion the approval threshold held back. Nobody on
     * the desk chose the rate — it is the published FxRate captured at submission —
     * and the order never expires, so the rate stays locked however long it rests.
     */
    approvalRequired?: boolean;
    isForward?: boolean;
    forwardDate?: string | null;
    adminId?: string;
    /** Set when the operator raised it with `requireConfirmation: false`; the client was never asked. */
    confirmationSkipped?: boolean;
    cancelReason?: string;
    [key: string]: any;
  };
  /** List rows also carry generic fields (email, lockedBy, ...). */
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class FxOrderService {
  private readonly env = inject(SHARED_ENV);
  private readonly hostUrl: string;

  constructor(private networkHelper: NetworkHelper) {
    this.hostUrl = this.env.host;
  }

  /**
   * Read-only preview — no userId, nothing is persisted, nothing is frozen. Always
   * call this instead of multiplying amount * rate in the browser: the backend
   * rounds the derived leg down to the target currency's amountPrecision, which the
   * panel does not know.
   */
  estimateFxOrder(data: FxEstimateRequest): Observable<FxEstimateResponse> {
    return this.networkHelper.post(`${this.hostUrl}/fxrates/estimate`, data);
  }

  /**
   * Raises the order and freezes the client's `from` funds.
   *
   * With `requireConfirmation` left out or `true` it comes back as
   * `pending_confirmation` and the 5-minute window starts; with `false` it comes back
   * as `new` — already accepted, never expiring, waiting only to be settled. Either
   * way, display the amounts from THIS response, not the ones that were typed.
   */
  createFxOrder(data: CreateFxOrderRequest): Observable<FxOrder> {
    return this.networkHelper.post(`${this.hostUrl}/fxrates/orders`, data);
  }

  /**
   * Settle. Takes no body — it settles exactly the rate and amounts the client
   * approved. Only valid while the order is in `new`, so refetch the row
   * immediately before calling it.
   */
  confirmFxOrderByAdmin(orderId: string): Observable<FxOrder> {
    return this.networkHelper.put(
      `${this.hostUrl}/fxrates/orders/${encodeURIComponent(
        orderId
      )}/confirm-by-admin`,
      {}
    );
  }

  /**
   * Allowed from `pending_confirmation` and `new`, never once settled. Releases the
   * freeze. The client IS notified (`fx-order-canceled`), for `new` as well as
   * `pending_confirmation` — pulling a quote they already accepted is more worth
   * telling them about, not less.
   */
  cancelFxOrder(orderId: string): Observable<FxOrder> {
    return this.networkHelper.delete(
      `${this.hostUrl}/fxrates/orders/${encodeURIComponent(orderId)}`,
      {}
    );
  }
}
